import { useState } from "react"
import { useI18n } from "../i18n/index.tsx"
import { activeView, canSave, type SavedView } from "../lib/saved-views.ts"
import { Button, Icon, Input } from "../ui/index.ts"

/**
 * 자주 보는 조건에 이름을 붙여 두고 한 번에 되돌아간다.
 *
 * 조건이 전부 주소에 있으므로(`lib/case-filters.ts`) 뷰를 누르는 일은 그 주소로 가는 일이다.
 * 이름을 묻는 데 `prompt()` 를 쓰지 않는다 — 브라우저가 막는 경우가 있고, 모바일에서 특히 거칠다.
 */
export function SavedViews({
  views,
  search,
  onApply,
  onSave,
  onRemove,
}: {
  views: readonly SavedView[]
  /** 지금 걸린 조건(`location.search`). */
  search: string
  onApply: (view: SavedView) => void
  onSave: (name: string) => void
  onRemove: (name: string) => void
}) {
  const { t } = useI18n()
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState("")

  const current = activeView(views, search)
  const saveable = canSave(search)

  function submit() {
    if (name.trim() === "") return
    onSave(name)
    setName("")
    setNaming(false)
  }

  if (views.length === 0 && !saveable) return null

  return (
    <div className="views">
      {views.length > 0 ? (
        <>
          <span className="views__label">{t("views.title")}</span>
          {views.map((view) => (
            <span
              key={view.name}
              className={view === current ? "view-chip is-current" : "view-chip"}
            >
              <button type="button" onClick={() => onApply(view)}>
                {view.name}
              </button>
              <button
                type="button"
                aria-label={`${view.name} ${t("views.remove")}`}
                title={t("views.remove")}
                onClick={() => onRemove(view.name)}
              >
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
        </>
      ) : null}

      {/* 조건이 하나도 없으면 저장할 것이 없다 — 전체 목록에 이름을 붙일 이유가 없다. */}
      {saveable && !naming ? (
        <Button size="sm" icon="plus" onClick={() => setNaming(true)}>
          {t("views.save")}
        </Button>
      ) : null}

      {naming ? (
        <span className="views__form">
          <Input
            aria-label={t("views.nameLabel")}
            placeholder={t("views.nameLabel")}
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit()
              if (event.key === "Escape") setNaming(false)
            }}
          />
          <Button size="sm" variant="primary" disabled={name.trim() === ""} onClick={submit}>
            {t("common.save")}
          </Button>
          <Button size="sm" onClick={() => setNaming(false)}>
            {t("common.cancel")}
          </Button>
        </span>
      ) : null}
    </div>
  )
}
